import memoize from 'memoize-one';

class UnreadCommentsIntersectionObserver {
	observer: IntersectionObserver | null = null;
	commentTimersMap = new Map<string, NodeJS.Timeout>();
	observedElementsSet = new Set<HTMLElement>();
	intersectionCallbacks: Array<(commentId: string) => void> = [];

	constructor() {
		this.createObserver();
	}

	createObserver() {
		try {
			if (typeof IntersectionObserver === 'undefined') {
				return;
			}

			this.observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						const commentId = entry.target.getAttribute('data-comment-id');
						if (!commentId) return;

						if (entry.isIntersecting) {
							// Comment became visible - set new timer for this specific comment
							const timer = setTimeout(() => {
								this.intersectionCallbacks.forEach((callback) => {
									callback(commentId);
								});
								// Clear the timer for this comment
								this.commentTimersMap.delete(commentId);

								// Unobserve this element since it's been processed
								if (this.observer) {
									this.observer.unobserve(entry.target);
									this.observedElementsSet.delete(entry.target as HTMLElement);
								}
							}, 2000); // 2 seconds default

							this.commentTimersMap.set(commentId, timer);
						} else {
							// Comment became invisible - clear timer for this comment
							if (this.commentTimersMap.has(commentId)) {
								clearTimeout(this.commentTimersMap.get(commentId)!);
								this.commentTimersMap.delete(commentId);
							}
						}
					});
				},
				{
					threshold: [0.25],
					// Add a custom name to identify this as the unread comments observer
					// This helps in tests to distinguish it from other intersection observers
					name: 'unreadCommentsObserver',
				} as IntersectionObserverInit & { name?: string },
			);
		} catch {
			this.observer = null;
		}
	}

	observeComment(targetElement: HTMLElement) {
		// don't observe if the observer is not initialized
		if (!this.observer) {
			return;
		}
		// don't observe if the targetElement is not a valid element
		if (!targetElement) {
			return;
		}
		// don't observe the same element twice
		if (this.observedElementsSet.has(targetElement)) {
			return;
		}
		this.observer.observe(targetElement);
		this.observedElementsSet.add(targetElement);
	}

	unobserveComment(targetElement: HTMLElement) {
		// don't unobserve if the observer is not initialized
		if (!this.observer) {
			return;
		}
		// don't unobserve if the targetElement is not a valid element
		if (!targetElement) {
			return;
		}
		// only unobserve if we're actually observing this element
		if (!this.observedElementsSet.has(targetElement)) {
			return;
		}

		// Clear any running timer for this comment
		const commentId = targetElement.getAttribute('data-comment-id');
		if (commentId && this.commentTimersMap.has(commentId)) {
			clearTimeout(this.commentTimersMap.get(commentId)!);
			this.commentTimersMap.delete(commentId);
		}

		this.observer.unobserve(targetElement);
		this.observedElementsSet.delete(targetElement);
	}

	// By exposing this function, we allow consumers to register callbacks
	registerIntersectionCallback(callback: (commentId: string) => void) {
		this.intersectionCallbacks.push(callback);
	}

	// Reconnect the observer if it was disconnected
	reconnect() {
		if (!this.observer) {
			this.createObserver();
		}
	}

	// Clean up method
	disconnect() {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}

		// Clear all timers
		this.commentTimersMap.forEach((timer) => clearTimeout(timer));
		this.commentTimersMap.clear();

		// Clear observed elements
		this.observedElementsSet.clear();

		// Clear callbacks
		this.intersectionCallbacks = [];
	}
}

// memoize ensures an IntersectionObserver is instantiated only once
const getStableIntersectionObserver = memoize(() => {
	return new UnreadCommentsIntersectionObserver();
});

export const getUnreadCommentsIntersectionObserver = () => {
	const intersectionObserver = getStableIntersectionObserver();
	intersectionObserver.reconnect();

	return intersectionObserver;
};
